import { useState, useCallback, useMemo } from 'react';
import { FilterMatchMode } from 'primereact/api';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGetTechnicalSupportRequestsQuery } from '../../../../api/endpoints/technicalSupportRequest';

const INITIAL_FILTER_STATE = {
    createdDate: null,
    createdDateMatchMode: FilterMatchMode.DATE_IS,
    solutionDate: null,
    solutionDateMatchMode: FilterMatchMode.DATE_IS,
    chassisNo: null,
    chassisNoMatchMode: FilterMatchMode.CONTAINS,
    vehicleModelEmissions: null,
    vehicleModelEmissionsMatchMode: FilterMatchMode.CONTAINS,
    modelYear: null,
    modelYearMatchMode: FilterMatchMode.CONTAINS,
    dealerName: null,
    dealerNameMatchMode: FilterMatchMode.CONTAINS,
    country: null,
    countryMatchMode: FilterMatchMode.EQUALS,
    userNameSurname: null,
    userNameSurnameMatchMode: FilterMatchMode.CONTAINS,
    errorDescription: null,
    errorDescriptionMatchMode: FilterMatchMode.CONTAINS,
    technicalResponsibleUserName: null,
    technicalResponsibleUserNameMatchMode: FilterMatchMode.CONTAINS,
    errorEffect: null,
    statusNotification: [],
    statusNotificationInf: null,
    mainSystemLookupCode: null,
    altSystemLookupCode: null,
    regionalManagerUserIds: [],
    technicalResponsibleUserIds: [],
    isVor: null
};

const BACKEND_COLUMN_MAP = {
    id: 'Id',
    createdDate: 'CreatedDate',
    solutionDate: 'SolutionDate',
    chassisNo: 'ChassisNo',
    vehicleModelEmissions: 'VehicleModelEmissions',
    modelYear: 'ModelYear',
    dealerName: 'DealerName',
    userNameSurname: 'UserNameSurname',
    errorDescription: 'ErrorDescription',
    technicalResponsibleUserName: 'TechnicalResponsibleUserName',
    errorEffect: 'ErrorEffect',
    statusNotification: 'StatusNotification',
    statusNotificationInf: 'StatusNotificationInf',
    gunFarki: 'GunFarki',
    mainSystemLookupCode: 'MainSystemLookupCode',
    altSystemLookupCode: 'AltSystemLookupCode',
    regionalManagerUserIds: 'TechnicalSupportRegionalManagerUserId',
    technicalResponsibleUserIds: 'TechnicalSupportResponsibleUserId',
    isVor: 'IsVor'
};

const MATCH_MODE_TO_OPERATOR = {
    [FilterMatchMode.CONTAINS]: 1,
    [FilterMatchMode.NOT_CONTAINS]: 2,
    [FilterMatchMode.STARTS_WITH]: 3,
    [FilterMatchMode.ENDS_WITH]: 4,
    [FilterMatchMode.EQUALS]: 5,
    [FilterMatchMode.NOT_EQUALS]: 6,
    [FilterMatchMode.DATE_IS]: 5,
    [FilterMatchMode.DATE_IS_NOT]: 6,
    [FilterMatchMode.DATE_AFTER]: 7,
    [FilterMatchMode.DATE_BEFORE]: 8
};

const getBackendColumnName = (field) => BACKEND_COLUMN_MAP[field] || null;
const getOperatorValue = (matchMode) => MATCH_MODE_TO_OPERATOR[matchMode] || MATCH_MODE_TO_OPERATOR[FilterMatchMode.CONTAINS];

const toIsoDateOrNull = (value) => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toISOString();
};

const normalizeTextOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const normalized = String(value).trim();
    return normalized || null;
};

const normalizeUserIds = (value) => (Array.isArray(value) ? value.map((item) => String(item ?? '').trim()).filter((item) => item !== '') : []);

const normalizeMultiSelectValues = (value) => (Array.isArray(value) ? value.filter((item) => item !== null && item !== undefined && item !== '') : value === null || value === undefined || value === '' ? [] : [value]);

const normalizeFilters = (updatedFilters) => ({
    createdDate: updatedFilters.createdDate || null,
    createdDateMatchMode: updatedFilters.createdDateMatchMode || FilterMatchMode.DATE_IS,
    solutionDate: updatedFilters.solutionDate || null,
    solutionDateMatchMode: updatedFilters.solutionDateMatchMode || FilterMatchMode.DATE_IS,
    chassisNo: updatedFilters.chassisNo || null,
    chassisNoMatchMode: updatedFilters.chassisNoMatchMode || FilterMatchMode.CONTAINS,
    vehicleModelEmissions: updatedFilters.vehicleModelEmissions || null,
    vehicleModelEmissionsMatchMode: updatedFilters.vehicleModelEmissionsMatchMode || FilterMatchMode.CONTAINS,
    modelYear: updatedFilters.modelYear ?? null,
    modelYearMatchMode: updatedFilters.modelYearMatchMode || FilterMatchMode.CONTAINS,
    dealerName: updatedFilters.dealerName || null,
    dealerNameMatchMode: updatedFilters.dealerNameMatchMode || FilterMatchMode.CONTAINS,
    country: updatedFilters.country || null,
    countryMatchMode: FilterMatchMode.EQUALS,
    userNameSurname: updatedFilters.userNameSurname || null,
    userNameSurnameMatchMode: updatedFilters.userNameSurnameMatchMode || FilterMatchMode.CONTAINS,
    errorDescription: updatedFilters.errorDescription || null,
    errorDescriptionMatchMode: updatedFilters.errorDescriptionMatchMode || FilterMatchMode.CONTAINS,
    technicalResponsibleUserName: updatedFilters.technicalResponsibleUserName || null,
    technicalResponsibleUserNameMatchMode: updatedFilters.technicalResponsibleUserNameMatchMode || FilterMatchMode.CONTAINS,
    errorEffect: updatedFilters.errorEffect || null,
    statusNotification: normalizeMultiSelectValues(updatedFilters.statusNotification),
    statusNotificationInf: normalizeTextOrNull(updatedFilters.statusNotificationInf),
    mainSystemLookupCode: updatedFilters.mainSystemLookupCode || null,
    altSystemLookupCode: updatedFilters.altSystemLookupCode || null,
    regionalManagerUserIds: normalizeUserIds(updatedFilters.regionalManagerUserIds),
    technicalResponsibleUserIds: normalizeUserIds(updatedFilters.technicalResponsibleUserIds),
    isVor: updatedFilters.isVor ?? null
});

const buildDirectFilterParams = (activeFilters) => {
    const regionalManagerIds = normalizeUserIds(activeFilters?.regionalManagerUserIds);
    const technicalResponsibleIds = normalizeUserIds(activeFilters?.technicalResponsibleUserIds);

    return {
        CreatedDate: toIsoDateOrNull(activeFilters?.createdDate),
        ChassisNo: normalizeTextOrNull(activeFilters?.chassisNo),
        VehicleModelEmissions: normalizeTextOrNull(activeFilters?.vehicleModelEmissions),
        DealerName: normalizeTextOrNull(activeFilters?.dealerName),
        Country: normalizeTextOrNull(activeFilters?.country),
        UserNameSurname: normalizeTextOrNull(activeFilters?.userNameSurname),
        ErrorDescription: normalizeTextOrNull(activeFilters?.errorDescription),
        ErrorEffect: activeFilters?.errorEffect === null || activeFilters?.errorEffect === undefined || activeFilters?.errorEffect === '' ? null : activeFilters.errorEffect,
        MainSystemCode: normalizeTextOrNull(activeFilters?.mainSystemLookupCode),
        SubSystemCode: normalizeTextOrNull(activeFilters?.altSystemLookupCode),
        Status: Array.isArray(activeFilters?.statusNotification) ? null : activeFilters?.statusNotification ?? null,
        StatusNotificationInf: normalizeTextOrNull(activeFilters?.statusNotificationInf),
        RegionalManagerName: regionalManagerIds.length > 0 ? regionalManagerIds.join(',') : null,
        TechnicalResponsibleName: technicalResponsibleIds.length > 0 ? technicalResponsibleIds.join(',') : null,
        IsVor: activeFilters?.isVor === null || activeFilters?.isVor === undefined ? null : activeFilters.isVor
    };
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];

    const pushFilter = (field, value, matchMode = FilterMatchMode.CONTAINS) => {
        if (Array.isArray(value)) {
            const cleanedValues = value.map((item) => String(item ?? '').trim()).filter((item) => item !== '');
            if (cleanedValues.length === 0) return;

            const column = getBackendColumnName(field);
            if (!column) return;

            cleanedValues.forEach((singleValue) => {
                filters.push({
                    column,
                    operator: getOperatorValue(matchMode || FilterMatchMode.EQUALS),
                    value: singleValue
                });
            });
            return;
        }

        if (value === null || value === undefined || value === '') return;
        const column = getBackendColumnName(field);
        if (!column) return;

        const filterValue = String(value).trim();
        if (!filterValue) return;

        filters.push({
            column,
            operator: getOperatorValue(matchMode),
            value: filterValue
        });
    };

    const createdDate = toIsoDateOrNull(filtersState?.createdDate);
    if (createdDate) {
        pushFilter('createdDate', createdDate, filtersState?.createdDateMatchMode || FilterMatchMode.DATE_IS);
    }

    const solutionDate = toIsoDateOrNull(filtersState?.solutionDate);
    if (solutionDate) {
        pushFilter('solutionDate', solutionDate, filtersState?.solutionDateMatchMode || FilterMatchMode.DATE_IS);
    }

    pushFilter('chassisNo', filtersState?.chassisNo, filtersState?.chassisNoMatchMode);
    pushFilter('vehicleModelEmissions', filtersState?.vehicleModelEmissions, filtersState?.vehicleModelEmissionsMatchMode);
    pushFilter('modelYear', filtersState?.modelYear, filtersState?.modelYearMatchMode);
    pushFilter('dealerName', filtersState?.dealerName, filtersState?.dealerNameMatchMode);
    pushFilter('userNameSurname', filtersState?.userNameSurname, filtersState?.userNameSurnameMatchMode);
    pushFilter('errorDescription', filtersState?.errorDescription, filtersState?.errorDescriptionMatchMode);
    pushFilter('technicalResponsibleUserName', filtersState?.technicalResponsibleUserName, filtersState?.technicalResponsibleUserNameMatchMode);
    pushFilter('errorEffect', filtersState?.errorEffect, FilterMatchMode.EQUALS);
    pushFilter('statusNotification', filtersState?.statusNotification, FilterMatchMode.EQUALS);
    pushFilter('statusNotificationInf', filtersState?.statusNotificationInf, FilterMatchMode.EQUALS);
    pushFilter('mainSystemLookupCode', filtersState?.mainSystemLookupCode, FilterMatchMode.EQUALS);
    pushFilter('altSystemLookupCode', filtersState?.altSystemLookupCode, FilterMatchMode.EQUALS);
    pushFilter('isVor', filtersState?.isVor, FilterMatchMode.EQUALS);

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || order === 0) return null;
    const column = getBackendColumnName(field);
    if (!column) return null;

    return [
        {
            column,
            direction: order === 1 ? 1 : 2
        }
    ];
};

const useTechnicalSupportRequestData = (dealerId) => {
    const { getCurrentLanguageId } = useLanguage();

    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 1
    });
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(0);
    const [resetCounter, setResetCounter] = useState(0);

    const queryArgs = useMemo(() => {
        const args = {
            activeOnly: 1,
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            languageId: getCurrentLanguageId(),
            ...buildDirectFilterParams(activeFilters)
        };

        if (dealerId !== null && dealerId !== undefined && dealerId !== '') {
            args.dealerId = dealerId;
        }

        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) {
            args.filters = filters;
        }

        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts && sorts.length > 0) {
            args.sorts = sorts;
        }

        return args;
    }, [activeFilters, dealerId, getCurrentLanguageId, lazyParams.page, lazyParams.rows, sortField, sortOrder]);

    const { data: response, isLoading, refetch, error } = useGetTechnicalSupportRequestsQuery(queryArgs);

    const onPage = useCallback((event) => {
        setLazyParams({
            first: event.first,
            rows: event.rows,
            page: event.page + 1
        });
    }, []);

    const handleFilterChange = useCallback((updatedFilters) => {
        setActiveFilters(normalizeFilters(updatedFilters));
        setLazyParams((prev) => ({
            first: 0,
            rows: prev.rows,
            page: 1
        }));
    }, []);

    const handleSortChange = useCallback((event) => {
        const nextSortField = event?.sortField || null;
        const nextSortOrder = event?.sortOrder || 0;
        const backendColumn = getBackendColumnName(nextSortField);

        if (!backendColumn && nextSortOrder !== 0) {
            return;
        }

        setSortField(nextSortField);
        setSortOrder(nextSortOrder);
        setLazyParams((prev) => ({
            first: 0,
            rows: prev.rows,
            page: 1
        }));
    }, []);

    const initFilters = useCallback(() => {
        setActiveFilters({
            ...INITIAL_FILTER_STATE,
            statusNotification: [],
            regionalManagerUserIds: [],
            technicalResponsibleUserIds: [],
            isVor: null
        });
        setSortField(null);
        setSortOrder(0);
        setLazyParams({
            first: 0,
            rows: 10,
            page: 1
        });
        setResetCounter((prev) => prev + 1);
    }, []);

    const exportParams = useMemo(() => {
        const params = { compressionType: 0 };

        const filters = buildDynamicFilters(activeFilters);
        if (filters.length > 0) params.filters = filters;

        const sorts = buildDynamicSorts(sortField, sortOrder);
        if (sorts && sorts.length > 0) params.sorts = sorts;

        return params;
    }, [activeFilters, sortField, sortOrder]);

    return {
        requests: response?.items || [],
        loading: isLoading,
        totalCount: response?.totalCount || 0,
        lazyParams,
        onPage,
        activeFilters,
        handleFilterChange,
        initFilters,
        sortField,
        sortOrder,
        onSort: handleSortChange,
        resetCounter,
        refetch,
        error,
        exportParams
    };
};

export default useTechnicalSupportRequestData;
